import { orgAPI } from "@/api/api";
import {
  useCopyFileRecord,
  useMoveFileRecord,
  type FileRecordListScope,
} from "@/api/fileRecords";
import type {
  ResolvedObjectAction,
  TransferDestination,
} from "@/object-actions/types";
import { moveRecordCascading } from "@/pages/workspaces/utils/folderMove";
import type { FileRecord } from "@cohesive/api-client";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import {
  attachFileRecordTransferSubmenus,
  type TransferSubmenuCommits,
} from "./attachFileRecordTransferSubmenus";
import { buildFolderCopyPlan } from "./copyCascade";
import { disambiguateLeaf, folderLeafNames, leafNameFromPath } from "./paths";
import { useAttachAddToLibrarySubmenu } from "./useAddToLibrarySubmenu";
import { useWritableWorkspaceIds } from "./useWritableWorkspaces";

/** The record's own list scope — source side of a move (cache patching + cascade). */
export function fileRecordListScope(
  record: FileRecord | null,
): FileRecordListScope | undefined {
  if (!record) return undefined;
  if (record.class === "workspace" && record.workspace_id) {
    return { class: "workspace", workspace_id: record.workspace_id };
  }
  if (record.class === "canvas" && record.canvas_id) {
    return { class: "canvas", canvas_id: record.canvas_id };
  }
  if (record.class === "flow-session" && record.flow_session_id) {
    return { class: "flow-session", flow_session_id: record.flow_session_id };
  }
  return undefined;
}

/**
 * Commit callbacks for the inline destination submenu. Both fire-and-forget:
 * the menu closes immediately and the outcome lands as a toast, matching the
 * drag-and-drop transfer semantics.
 *
 * Copy pre-resolves a unique leaf in the destination folder (the backend 409s
 * on exact path collisions — same mitigation as `CopyToWorkspaceDialog`).
 * Move keeps the record's name and cascades descendants for folders
 * (`moveRecordCascading`); a name collision surfaces as an error toast.
 */
export function useFileRecordTransferCommits(
  record: FileRecord | null,
): TransferSubmenuCommits {
  const sourceScope = useMemo(() => fileRecordListScope(record), [record]);
  const moveFileRecord = useMoveFileRecord(sourceScope);
  const copyFileRecord = useCopyFileRecord(undefined);

  const commitCopy = useCallback(
    (destination: TransferDestination) => {
      if (!record?.id) return;
      void (async () => {
        try {
          // The record handed in (e.g. a canvas entity) can be a minimal
          // shape without `path`; fetch the authoritative record so the copy
          // lands under its real filename.
          const sourcePath =
            record.path || (await orgAPI.fileRecords.getById(record.id)).path;
          const leaf = leafNameFromPath(sourcePath) || "file";
          const destRecords = await orgAPI.fileRecords.list({
            class: "workspace",
            workspace_id: destination.workspaceId,
          });
          const name = disambiguateLeaf(
            leaf,
            folderLeafNames(destRecords, destination.folderPath),
          );
          // A folder copy is the marker plus every descendant: the server
          // copies one row per call and cannot see a folder's contents, so
          // issuing only the marker would land an empty folder.
          const isFolder = sourcePath.endsWith("/");
          const scope = fileRecordListScope(record);
          const allRecords =
            isFolder && scope ? await orgAPI.fileRecords.list(scope) : [];
          const plan = buildFolderCopyPlan({
            folder: { ...record, path: sourcePath },
            allRecords,
            destinationFolderPath: destination.folderPath,
            leaf: name,
          });
          for (const entry of plan) {
            await copyFileRecord.mutateAsync({
              fileRecordId: entry.fileRecordId,
              payload: {
                destination: {
                  class: "workspace",
                  workspace_id: destination.workspaceId,
                  path: entry.path,
                },
              },
            });
          }
          toast.success(`Copied to ${destination.label}`);
        } catch {
          toast.error("Failed to copy file");
        }
      })();
    },
    [copyFileRecord, record],
  );

  const commitMove = useCallback(
    (destination: TransferDestination) => {
      if (!record?.id || !record.path) return;
      void (async () => {
        try {
          const isFolder = record.path.endsWith("/");
          const leaf = leafNameFromPath(record.path);
          const scope = fileRecordListScope(record);
          // Folder moves cascade over every descendant; fetch the source
          // scope's records so the cascade can re-path them.
          const allRecords =
            isFolder && scope ? await orgAPI.fileRecords.list(scope) : [];
          await moveRecordCascading({
            record,
            payload: {
              destination: {
                class: "workspace",
                workspace_id: destination.workspaceId,
                path: `${destination.folderPath}${leaf}${isFolder ? "/" : ""}`,
                is_folder: isFolder,
              },
            },
            allRecords,
            moveOne: (vars) => moveFileRecord.mutateAsync(vars),
          });
          toast.success(`Moved to ${destination.label}`);
        } catch {
          toast.error("Failed to move file");
        }
      })();
    },
    [moveFileRecord, record],
  );

  return useMemo(() => ({ commitCopy, commitMove }), [commitCopy, commitMove]);
}

/**
 * Decorate resolved actions with destination submenus for `move` /
 * `copy-to`. Safe to call with a null record (returns actions
 * unchanged) so hook order stays stable at call sites.
 */
export function useAttachFileRecordTransferSubmenus(
  actions: ResolvedObjectAction[],
  record: FileRecord | null,
): ResolvedObjectAction[] {
  // Add to Library rides the same chain: every surface that resolves file
  // actions gets its cascade too, locked to the container's home workspace.
  const withAddToLibrary = useAttachAddToLibrarySubmenu(actions, record);
  actions = withAddToLibrary;
  const commits = useFileRecordTransferCommits(record);
  // Only worth asking once a transfer action is actually on the menu.
  const hasTransfer = actions.some(
    (a) => a.id === "move" || a.id === "copy-to",
  );
  const writableWorkspaceIds = useWritableWorkspaceIds(hasTransfer);
  return useMemo(() => {
    const attached = attachFileRecordTransferSubmenus(actions, record, commits);
    if (!writableWorkspaceIds) return attached;
    return attached.map((action) =>
      action.submenu
        ? { ...action, submenu: { ...action.submenu, writableWorkspaceIds } }
        : action,
    );
  }, [actions, commits, record, writableWorkspaceIds]);
}
